import type { LogbookClass, LogbookEntry } from '@/lib/db';
import { mapColumns, parseCsv, stringifyCsv } from '@/lib/parse/csv';

import { ENGINE_CLASS_BY_TEXT, ENGINE_CLASS_SHORT, formatIntermediate } from '../columns';
import { blankLandings, blankTakeoffs, blankTimeMatrix } from '../entry';
import { formatDecimalHours, parseDurationHours } from '../time';
import { intermediateFrom } from './route';
import type { ImportParseResult, ParsedLogbookRow } from './types';

/**
 * Flight Data Viewer's own CSV — full fidelity, both directions. Every field
 * `LOGBOOK_COLUMNS` shows round-trips through it exactly, which is what makes
 * it the answer for a spreadsheet edit-and-reimport, and for any logbook app
 * whose own importer maps arbitrary columns by hand (LogTen Pro's importer is
 * exactly this shape — see `docs/PHASE-6.md` — so this format is what a pilot
 * hands it rather than a bespoke LogTen export this app cannot verify against
 * a real LogTen install).
 */

const HEADER = [
  'Date',
  'From',
  'Intermediate',
  'To',
  'Aircraft Type',
  'Registration',
  'Class',
  'PIC',
  'Co-Pilot / Student',
  'Details of Flight',
  'Day Hr',
  'Night Hr',
  'Dual Hr',
  'PIC Hr',
  'Co-Pilot Hr',
  'Command Practice Hr',
  'Actual Instrument Hr',
  'Simulated Instrument Hr',
  'Ground Instrument Hr',
  'Non-Precision Approaches',
  'Precision Approaches',
  'Flight Instructor Hr',
  'IFR Cross Country Hr',
  'Takeoffs Day',
  'Takeoffs Night',
  'Landings Day',
  'Landings Night',
  'Touch And Go',
  'Confirmed',
] as const;

export function exportNativeCsv(entries: readonly LogbookEntry[]): string {
  const rows: (string | number)[][] = [[...HEADER]];
  for (const entry of entries) {
    rows.push([
      entry.date,
      entry.from ?? '',
      formatIntermediate(entry.intermediate),
      entry.to ?? '',
      entry.aircraftType ?? '',
      entry.registration ?? '',
      ENGINE_CLASS_SHORT[entry.time.engineClass],
      entry.pic,
      entry.otherCrew ?? '',
      entry.remarks ?? '',
      formatDecimalHours(entry.time.dayHr),
      formatDecimalHours(entry.time.nightHr),
      formatDecimalHours(entry.time.dualHr),
      formatDecimalHours(entry.time.picHr),
      formatDecimalHours(entry.time.coPilotHr),
      formatDecimalHours(entry.time.commandPracticeHr),
      formatDecimalHours(entry.time.actualInstrumentHr),
      formatDecimalHours(entry.time.simulatedInstrumentHr),
      formatDecimalHours(entry.time.groundInstrumentHr),
      entry.approaches.nonPrecision,
      entry.approaches.precision,
      formatDecimalHours(entry.time.instructorHr),
      formatDecimalHours(entry.time.ifrCrossCountryHr),
      entry.takeoffs.day,
      entry.takeoffs.night,
      entry.landings.day,
      entry.landings.night,
      entry.landings.touchAndGo,
      entry.confirmedAt === undefined ? 'No' : 'Yes',
    ]);
  }
  return stringifyCsv(rows);
}

type Field =
  | 'date'
  | 'registration'
  | 'aircraftType'
  | 'pic'
  | 'otherCrew'
  | 'from'
  | 'to'
  | 'intermediate'
  | 'engineClass'
  | 'dayHr'
  | 'nightHr'
  | 'picHr'
  | 'commandPracticeHr'
  | 'dualHr'
  | 'coPilotHr'
  | 'instructorHr'
  | 'actualInstrumentHr'
  | 'simulatedInstrumentHr'
  | 'groundInstrumentHr'
  | 'ifrCrossCountryHr'
  | 'nonPrecisionApproaches'
  | 'precisionApproaches'
  | 'takeoffsDay'
  | 'takeoffsNight'
  | 'landingsDay'
  | 'landingsNight'
  | 'touchAndGo'
  | 'remarks';

const SYNONYMS: Readonly<Record<Field, readonly string[]>> = {
  date: ['date'],
  registration: ['registration', 'aircraft', 'tail number', 'aircraftid'],
  aircraftType: ['aircraft type', 'type'],
  pic: ['pic', 'pilot in command'],
  otherCrew: ['other crew', 'crew', 'passengers'],
  from: ['from'],
  to: ['to'],
  intermediate: ['intermediate', 'via', 'route'],
  engineClass: ['class', 'engine class'],
  dayHr: ['day hr', 'day'],
  nightHr: ['night hr', 'night'],
  picHr: ['pic hr', 'p1'],
  commandPracticeHr: ['command practice hr', 'command practice', 'icus hr', 'icus'],
  dualHr: ['dual hr', 'dual'],
  coPilotHr: ['co pilot hr', 'co pilot', 'sic hr', 'sic'],
  instructorHr: ['flight instructor hr', 'instructor hr', 'instructing', 'cfi hr', 'cfi'],
  actualInstrumentHr: ['actual instrument hr', 'instrument', 'imc'],
  simulatedInstrumentHr: ['simulated instrument hr', 'hood', 'sim imc'],
  groundInstrumentHr: [
    'ground instrument hr',
    'ground',
    'synthetic trainer hr',
    'sft',
    'simulator',
  ],
  ifrCrossCountryHr: ['ifr cross country hr', 'ifr cross country', 'ifr xc'],
  nonPrecisionApproaches: ['non precision approaches', 'non precision', 'npa'],
  precisionApproaches: ['precision approaches', 'precision', 'pa'],
  takeoffsDay: ['takeoffs day', 'day takeoffs'],
  takeoffsNight: ['takeoffs night', 'night takeoffs'],
  landingsDay: ['landings day', 'day landings'],
  landingsNight: ['landings night', 'night landings'],
  touchAndGo: ['touch and go', 't g', 'touch and goes'],
  remarks: ['details of flight', 'remarks', 'comments', 'notes'],
};

function parseEngineClass(raw: string): LogbookClass {
  const match = ENGINE_CLASS_BY_TEXT.get(raw.trim().toLowerCase());
  return match ?? 'single-engine-land';
}

function parseIntOrZero(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return 0;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function cell(row: readonly string[], index: number): string {
  return index === -1 ? '' : (row[index] ?? '');
}

export function importNativeCsv(text: string): ImportParseResult {
  const csvRows = parseCsv(text);
  const [header, ...dataRows] = csvRows;
  if (header === undefined) {
    return { rows: [], errors: [] };
  }
  const columns = mapColumns(header, SYNONYMS);
  if (columns.date === -1) {
    return { rows: [], errors: [{ line: 1, message: 'No "Date" column found in this file.' }] };
  }

  const rows: ParsedLogbookRow[] = [];
  const errors: ImportParseResult['errors'] = [];

  dataRows.forEach((row, index) => {
    const line = index + 2;
    const date = cell(row, columns.date).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      errors.push({ line, message: `"${date}" is not a date in YYYY-MM-DD form.` });
      return;
    }

    const time = { ...blankTimeMatrix(parseEngineClass(cell(row, columns.engineClass))) };
    const durationFields: [Exclude<keyof typeof time, 'engineClass'>, Field][] = [
      ['dayHr', 'dayHr'],
      ['nightHr', 'nightHr'],
      ['picHr', 'picHr'],
      ['commandPracticeHr', 'commandPracticeHr'],
      ['dualHr', 'dualHr'],
      ['coPilotHr', 'coPilotHr'],
      ['instructorHr', 'instructorHr'],
      ['actualInstrumentHr', 'actualInstrumentHr'],
      ['simulatedInstrumentHr', 'simulatedInstrumentHr'],
      ['groundInstrumentHr', 'groundInstrumentHr'],
      ['ifrCrossCountryHr', 'ifrCrossCountryHr'],
    ];
    let rowOk = true;
    for (const [timeKey, field] of durationFields) {
      const raw = cell(row, columns[field]);
      const parsed = raw.trim() === '' ? 0 : parseDurationHours(raw);
      if (parsed === null) {
        errors.push({ line, message: `"${raw}" in the ${field} column is not a time.` });
        rowOk = false;
        continue;
      }
      time[timeKey] = parsed;
    }
    if (!rowOk) {
      return;
    }

    rows.push({
      date,
      ...(cell(row, columns.registration).trim() === ''
        ? {}
        : { registration: cell(row, columns.registration).trim().toUpperCase() }),
      ...(cell(row, columns.aircraftType).trim() === ''
        ? {}
        : { aircraftType: cell(row, columns.aircraftType).trim() }),
      ...(cell(row, columns.pic).trim() === '' ? {} : { pic: cell(row, columns.pic).trim() }),
      ...(cell(row, columns.from).trim() === '' ? {} : { from: cell(row, columns.from).trim() }),
      ...(cell(row, columns.to).trim() === '' ? {} : { to: cell(row, columns.to).trim() }),
      ...intermediateFrom(cell(row, columns.intermediate)),
      ...(cell(row, columns.remarks).trim() === ''
        ? {}
        : { remarks: cell(row, columns.remarks).trim() }),
      time,
      approaches: {
        nonPrecision: parseIntOrZero(cell(row, columns.nonPrecisionApproaches)),
        precision: parseIntOrZero(cell(row, columns.precisionApproaches)),
      },
      landings: {
        ...blankLandings(),
        day: parseIntOrZero(cell(row, columns.landingsDay)),
        night: parseIntOrZero(cell(row, columns.landingsNight)),
        touchAndGo: parseIntOrZero(cell(row, columns.touchAndGo)),
      },
      takeoffs: {
        ...blankTakeoffs(),
        day: parseIntOrZero(cell(row, columns.takeoffsDay)),
        night: parseIntOrZero(cell(row, columns.takeoffsNight)),
      },
    });
  });

  return { rows, errors };
}
